import React, {useCallback, useEffect, useState} from "react";
import Popup from "reactjs-popup";
import "reactjs-popup/dist/index.css";
import Styles from "./styles.module.scss";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

import chevronIcon from '../../../theme/assets/svg/tc_cab_capsule_chevron_icon.svg';
import plusIcon from '../../../theme/assets/svg/tc_cab_capsule_plus_icon.svg';
import notesIcon from '../../../theme/assets/svg/tc_cab_capsule_notes_icon.svg';

import {
    deleteCapsuleNote,
    getCapsuleNotes,
    saveCapsuleNote,
    setCapsuleNotesModalStatus
} from "../../../store/cabinet/action";
import {
    CAPSULE_SECTION_WEIGHT,
    capsuleSectionPointsHandler,
    isCapsuleEditable,
    noteDateHandler,
    noteExcerptHandler,
    noteTitleHandler,
    pluralHandler
} from "../../../helpers/capsule";

const CabinetCapsuleNotesModal = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const capsuleNotesModalStatus = useSelector(store => store.cabinet.capsuleNotesModalStatus);
    const capsuleNotes = useSelector(store => store.cabinet.capsuleNotes);
    const capsuleStats = useSelector(store => store.cabinet.capsuleStats);
    const saveCapsuleNoteStarted = useSelector(store => store.cabinet.saveCapsuleNoteStarted);
    const formErr = useSelector(store => store.cabinet.saveCapsuleNoteErr);

    // list — список заметок, edit — редактор одной заметки
    const [view, setView] = useState('list');
    const [editedNote, setEditedNote] = useState(false);
    const [text, setText] = useState('');
    const [isExitConfirmOpen, setIsExitConfirmOpen] = useState(false);

    const notes = capsuleNotes?.notes || [];
    const notesStats = capsuleStats?.stats?.notes;
    const isEditable = isCapsuleEditable(capsuleStats?.capsule_status);
    const isLimitReached = notesStats?.max ? notes.length >= notesStats.max : false;

    const contribution = capsuleSectionPointsHandler(capsuleStats?.stats, 'notes');

    useEffect(()=>{
        if (capsuleNotesModalStatus){
            dispatch(getCapsuleNotes());
        }
    },[capsuleNotesModalStatus, dispatch])

    const resetEditor = useCallback(()=>{
        setView('list');
        setEditedNote(false);
        setText('');
        setIsExitConfirmOpen(false);
    },[])

    const closeModal = () =>{
        resetEditor();
        dispatch(setCapsuleNotesModalStatus(false));
    }

    const openNoteHandler = (note) =>{
        setEditedNote(note);
        setText(note?.text || '');
        setView('edit');
    }

    const addNoteHandler = () =>{
        if (!isEditable || isLimitReached){
            return;
        }
        setEditedNote(false);
        setText('');
        setView('edit');
    }

    // текст изменился — просто так из редактора не выпускаем
    const isDirty = () =>{
        return text !== (editedNote?.text || '');
    }

    const backHandler = () =>{
        if (isDirty()){
            setIsExitConfirmOpen(true);
            return;
        }
        resetEditor();
    }

    const saveHandler = (afterSave) =>{
        if (saveCapsuleNoteStarted || !text.trim().length){
            return;
        }
        dispatch(saveCapsuleNote(editedNote?.id, text, ()=>{
            if (afterSave){
                afterSave();
            } else {
                resetEditor();
            }
        }));
    }

    const deleteHandler = () =>{
        if (!editedNote?.id){
            return;
        }
        dispatch(deleteCapsuleNote(editedNote.id, resetEditor));
    }

    return (
        <Popup
            open={capsuleNotesModalStatus}
            modal
            nested
            className="capsuleModal"
            closeOnDocumentClick
            onClose={closeModal}
        >
            <div className={`${Styles.modalContainer}`}>
                <div className={`${Styles.modalCloseBtn}`} onClick={closeModal}>
                    <div/>
                    <div/>
                </div>

                {view === 'list' ? (
                    <div className={`${Styles.modalContentWrap}`}>
                        <div className={`${Styles.modalHead}`}>
                            <div className={`${Styles.modalTitle}`}>{t('Заметки')}</div>
                            <div className={`${Styles.modalNote}`}>
                                {t('Свободный текст о вас — дополняет анкету и памятные материалы')}
                            </div>

                            <div className={`${Styles.sectionProgress}`}>
                                <div className={`${Styles.sectionProgressLabels}`}>
                                    <span>{t('Вклад в прогресс капсулы')}</span>
                                    <span className={`${Styles.sectionProgressValue}`}>
                                        {contribution} / {CAPSULE_SECTION_WEIGHT.notes}%
                                    </span>
                                </div>
                                <div className={`${Styles.sectionProgressBar}`}>
                                    <div
                                        className={`${Styles.sectionProgressFill}`}
                                        style={{width: `${Math.min(notesStats?.percent || 0, 100)}%`}}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className={`${Styles.modalBody}`}>
                            {notes.length ? (
                                <div className={`${Styles.notesList}`}>
                                    {notes.map((note)=>(
                                        <div
                                            className={`${Styles.noteRow}`}
                                            key={'capsule-note_' + note?.id}
                                            onClick={()=>{openNoteHandler(note)}}
                                        >
                                            <div className={`${Styles.noteRowPos}`}>
                                                <div className={`${Styles.noteRowTitle}`}>{noteTitleHandler(note?.text)}</div>
                                                <div className={`${Styles.noteRowMeta}`}>
                                                    <span className={`${Styles.noteRowDate}`}>{noteDateHandler(note?.created_at)}</span>
                                                    <span className={`${Styles.noteRowExcerpt}`}>{noteExcerptHandler(note?.text)}</span>
                                                </div>
                                            </div>
                                            <div className={`${Styles.noteRowChevron}`}>
                                                <img src={chevronIcon} alt=""/>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className={`${Styles.emptyWrap}`}>
                                    <div className={`${Styles.emptyIconWrap}`}>
                                        <img src={notesIcon} alt=""/>
                                    </div>
                                    <div className={`${Styles.emptyTitle}`}>{t('Добавьте первую заметку')}</div>
                                    <div className={`${Styles.emptyNote}`}>
                                        {t('Опишите привычки, взгляды, важные истории — всё, что поможет точнее сформировать вашу модель')}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className={`${Styles.modalFooter}`}>
                            <div className={`${Styles.modalFooterName}`}>
                                {notes.length} {pluralHandler(notes.length, [t('заметка'), t('заметки'), t('заметок')])}
                                {notesStats?.max ? ` ${t('из')} ${notesStats.max}` : null}
                            </div>
                            <div
                                className={`${Styles.modalBtn} ${!isEditable || isLimitReached ? Styles.modalBtnDsb : null}`}
                                onClick={addNoteHandler}
                            >
                                <img src={plusIcon} alt=""/>
                                <span>{t('Добавить заметку')}</span>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className={`${Styles.modalContentWrap}`}>
                        <div className={`${Styles.modalHead}`}>
                            <div className={`${Styles.backBtn}`} onClick={backHandler}>
                                <svg width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M6 1L1 6L6 11M1 6H15" stroke="#9EB2B8" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                <span>{t('Все заметки')}</span>
                            </div>
                            <div className={`${Styles.modalTitle}`}>
                                {editedNote ? t('Заметка') : t('Новая заметка')}
                            </div>
                            <div className={`${Styles.modalNote}`}>
                                {t('Первая строка станет заголовком. После сохранения заметка появится в списке')}
                            </div>
                        </div>

                        <div className={`${Styles.modalBody}`}>
                            <textarea
                                className={`${Styles.noteInput}`}
                                value={text}
                                placeholder={t('Начните писать…')}
                                onChange={(e)=>{setText(e.target.value)}}
                                maxLength={20000}
                                disabled={!isEditable}
                            />
                            <div className={`${Styles.tcErr} ${formErr?.errors?.text ? Styles.tcErrActive : null}`}>
                                {formErr?.errors?.text ? formErr.errors.text : null}
                            </div>
                        </div>

                        <div className={`${Styles.modalFooter}`}>
                            {editedNote && isEditable ? (
                                <div className={`${Styles.modalBtnDanger}`} onClick={deleteHandler}>
                                    {t('Удалить')}
                                </div>
                            ) : <div/>}
                            <div
                                className={`${Styles.modalBtn} ${!isEditable || !text.trim().length ? Styles.modalBtnDsb : null}`}
                                onClick={()=>{saveHandler()}}
                            >
                                <span>{t('Сохранить заметку')}</span>
                            </div>
                        </div>
                    </div>
                )}

                <Popup
                    open={isExitConfirmOpen}
                    modal
                    nested
                    className="capsuleConfirmModal"
                    closeOnDocumentClick
                    onClose={()=>{setIsExitConfirmOpen(false)}}
                >
                    <div className={`${Styles.confirmContainer}`}>
                        <div className={`${Styles.modalCloseBtn}`} onClick={()=>{setIsExitConfirmOpen(false)}}>
                            <div/>
                            <div/>
                        </div>
                        <div className={`${Styles.confirmTitle}`}>{t('Выход из заметки')}</div>
                        <div className={`${Styles.confirmNote}`}>
                            {t('Если выйти без сохранения, текст заметки будет утерян')}
                        </div>
                        <div className={`${Styles.modalBtn}`} onClick={()=>{saveHandler(resetEditor)}}>
                            <span>{t('Сохранить и выйти')}</span>
                        </div>
                        <div className={`${Styles.modalBtnDanger}`} onClick={resetEditor}>
                            {t('Выйти без сохранений')}
                        </div>
                    </div>
                </Popup>
            </div>
        </Popup>
    );
};

export default CabinetCapsuleNotesModal;
